/**
 * AlternativeTopicReview Component
 *
 * Students review a different topic's PDF while waiting for the Teacher to finish learning.
 * They assess the material complexity, providing useful research data.
 *
 * Two modes:
 * - "view": PDF viewer only (Round 2 - while Teacher learns)
 * - "rate": Material complexity assessment (Round 3 - while Teacher does pre-chat assessment)
 *
 * Props:
 * - mode: "view" or "rate"
 * - showInstructions: If true (in rate mode), also shows role instructions for upcoming chat
 *
 * Material Complexity Assessment (4-factor, 0-6 scale):
 * - Mental Effort: Cognitive load experienced while reading
 * - Complexity: Intrinsic difficulty of the subject matter
 * - Clarity: Quality of presentation/explanation
 * - Time Adequacy: Whether time was sufficient to understand
 *
 * Logic:
 * - Uses pairedTopic from server (set in callbacks.js) - ensures same subwave's topicPool
 * - Round 2: Shows PDF for reading
 * - Round 3: Asks complexity ratings + role instructions if showInstructions=true
 * - Saves responses to player scope for research analysis
 */
import React, { useState, useMemo, useEffect } from "react";
import { usePlayer, useGame, useRound } from "@empirica/core/player/classic/react";
import { parseObjectives } from "./ObjectivesChecklist";

export function AlternativeTopicReview({ mode = "view", showInstructions = false, next }) {
  const player = usePlayer();
  const game = useGame();
  const round = useRound();

  // Get current topic info
  const config = game?.get("config");
  const pairedConfig = game?.get("pairedConfig");
  // Learning objectives for the alternative topic — shown so the review is
  // anchored to the same key points the material is meant to cover.
  const alternativeObjectives = parseObjectives(pairedConfig?.instructionLearner || pairedConfig?.instructionExplainer);
  const topicName = config?.name || "the topic";

  // Get the paired topic from the server (set in callbacks.js onGameStart)
  // This ensures the paired topic is from the same subwave's topicPool
  const alternativeTopic = useMemo(() => {
    const pairedTopicId = game?.get("pairedTopic");
    const pairedTopicName = round?.get("pairedTopicName") || pairedConfig?.name;

    if (pairedTopicId) {
      return {
        id: pairedTopicId,
        name: pairedTopicName || pairedTopicId // Fallback to ID if name not yet available
      };
    }

    // If pairedTopic not set yet, show placeholder (should resolve quickly)
    console.warn("[AlternativeTopicReview] Waiting for pairedTopic from server...");
    return { id: null, name: 'Loading...' };
  }, [game, round, pairedConfig]);

  // Always show the normal PDF for alternative topic review
  // Students are assessing their prior knowledge of a different topic - not learning from AI
  const alternativePdfUrl = alternativeTopic.id
    ? `/pdfs/${alternativeTopic.id}.pdf#view=FitH`
    : null;

  // Form state - 4-factor material complexity assessment
  const [ratings, setRatings] = useState({
    mentalEffort: null,
    complexity: null,
    clarity: null,
    timeAdequacy: null,
  });
  const [submitted, setSubmitted] = useState(false);
  const [zoom, setZoom] = useState(100);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 50));
  const handleZoomReset = () => setZoom(100);

  // Load any previously saved ratings
  useEffect(() => {
    const saved = player?.get("alternativeTopicReview");
    if (saved) {
      setRatings(saved.ratings || ratings);
      setSubmitted(saved.submitted || false);
    }
  }, []);

  // Save the topic being viewed (even in view mode) so we know what they saw
  useEffect(() => {
    if (mode === "view" && alternativeTopic) {
      const existing = player?.get("alternativeTopicReview");
      if (!existing) {
        player.set("alternativeTopicReview", {
          topicId: alternativeTopic.id,
          topicName: alternativeTopic.name,
          ratings: { mentalEffort: null, complexity: null, clarity: null, timeAdequacy: null },
          submitted: false,
          viewedAt: new Date().toISOString(),
        });
      }
    }
  }, [mode, alternativeTopic, player]);

  const handleRatingChange = (question, value) => {
    const newRatings = { ...ratings, [question]: value };
    setRatings(newRatings);
    // Note: Only saved to DB on submit to reduce DB writes
  };

  // Save data on unmount (when phase ends via timer or partner ready)
  // This ensures we don't lose data if user doesn't click submit
  useEffect(() => {
    return () => {
      // Read the persisted record fresh: this cleanup also runs AFTER a
      // successful submit (next() unmounts the component) and used to
      // overwrite submitted:true with submitted:false, losing submittedAt.
      const existing = player?.get("alternativeTopicReview");
      if (existing?.submitted) return;

      // Save current state when component unmounts (merge to keep viewedAt)
      if (alternativeTopic?.id && Object.values(ratings).some(v => v !== null)) {
        player.set("alternativeTopicReview", {
          ...(existing || {}),
          topicId: alternativeTopic.id,
          topicName: alternativeTopic.name,
          ratings,
          submitted: false,
          timestamp: new Date().toISOString(),
        });
      }
    };
  }, [alternativeTopic, ratings, player]);

  const handleSubmit = () => {
    // Merge with the existing record so viewedAt (written in view mode) survives
    const existing = player?.get("alternativeTopicReview");
    player.set("alternativeTopicReview", {
      ...(existing || {}),
      topicId: alternativeTopic.id,
      topicName: alternativeTopic.name,
      ratings,
      submitted: true,
      submittedAt: new Date().toISOString(),
    });
    setSubmitted(true);
    // Call next to advance to the next stage
    if (next) {
      next();
    }
  };

  const allAnswered = Object.values(ratings).every((v) => v !== null);

  // 4-factor material complexity assessment questions
  const questions = [
    {
      key: "mentalEffort",
      label: "How much mental effort did this material require?",
      lowLabel: "Very little",
      highLabel: "Very high",
    },
    {
      key: "complexity",
      label: "How complex is the subject matter?",
      lowLabel: "Very simple",
      highLabel: "Very complex",
    },
    {
      key: "clarity",
      label: "How clear is the presentation of the material?",
      lowLabel: "Very unclear",
      highLabel: "Very clear",
    },
    {
      key: "timeAdequacy",
      label: "Was the time sufficient to understand this material?",
      lowLabel: "Not at all",
      highLabel: "More than enough",
    },
  ];

  // ============ VIEW MODE: Full-width PDF viewer ============
  if (mode === "view") {
    // Show loading state if paired topic not yet available from server
    if (!alternativeTopic.id) {
      return (
        <div className="h-full w-full flex items-center justify-center bg-slate-50">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-slate-300 border-t-slate-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Loading materials...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="h-full w-full bg-gradient-to-b from-slate-50 to-slate-100 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-4 py-3 shadow-sm">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-sm text-slate-600">
              Your partner is studying the main topic
            </span>
          </div>
          <h1 className="text-lg font-semibold text-slate-900">
            While you wait: Read through this material
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            You will rate it against the key points below in the next step.
          </p>
        </div>

        {/* Learning objectives for the alternative topic — anchors the review */}
        {alternativeObjectives.length > 0 && (
          <div className="bg-indigo-50 border-b border-indigo-200 px-4 py-3">
            <p className="text-xs font-semibold text-indigo-700 mb-1">
              Key points this material aims to cover — keep these in mind as you read:
            </p>
            <ul className="list-disc pl-5 space-y-0.5">
              {alternativeObjectives.map((o, i) => (
                <li key={i} className="text-xs text-indigo-900">{o.title}</li>
              ))}
            </ul>
          </div>
        )}

        {/* PDF Viewer - Full width */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tip Banner */}
          <div className="flex-shrink-0 bg-amber-50 border-b border-amber-200 px-3 py-2">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <p className="text-xs text-amber-700"><strong>Tip:</strong> Use zoom controls to adjust text size for better readability.</p>
            </div>
          </div>

          {/* Header with Zoom Controls */}
          <div className="bg-slate-800 text-white px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="font-medium text-sm">{alternativeTopic.name}</span>
            </div>
            {/* Zoom Controls */}
            <div className="flex items-center bg-slate-700 rounded">
              <button
                onClick={handleZoomOut}
                disabled={zoom <= 50}
                className="px-2 py-1 text-slate-300 hover:text-white hover:bg-slate-600 rounded-l transition-colors disabled:opacity-40"
                title="Zoom out"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <button
                onClick={handleZoomReset}
                className="px-2 py-1 text-xs font-medium text-slate-200 hover:bg-slate-600 transition-colors min-w-[45px]"
                title="Reset zoom"
              >
                {zoom}%
              </button>
              <button
                onClick={handleZoomIn}
                disabled={zoom >= 200}
                className="px-2 py-1 text-slate-300 hover:text-white hover:bg-slate-600 rounded-r transition-colors disabled:opacity-40"
                title="Zoom in"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>

          {/* PDF with zoom - Full width */}
          <div className="flex-1 overflow-auto bg-slate-100">
            <div
              style={{
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top center',
                width: zoom > 100 ? `${10000 / zoom}%` : '100%',
                height: zoom > 100 ? `${10000 / zoom}%` : '100%',
              }}
            >
              <iframe
                src={alternativePdfUrl}
                title={`${alternativeTopic.name} PDF`}
                className="w-full h-full border-0"
                style={{ minHeight: '100%' }}
              />
            </div>
          </div>
        </div>

        {/* Footer status */}
        <div className="bg-white border-t border-slate-200 px-4 py-2">
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span>Continue when ready, or wait for the timer</span>
          </div>
        </div>
      </div>
    );
  }

  // ============ RATE MODE: Show rating form (+ optional role instructions) ============
  return (
    <div className="h-full w-full bg-gradient-to-b from-slate-50 to-slate-100 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 shadow-sm">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-xl font-semibold text-slate-900">
            {showInstructions ? "Pre-Chat Preparation" : "Material Complexity Assessment"}
          </h1>
          <p className="text-slate-600 mt-1">
            {showInstructions
              ? "Complete the assessment and review your role for the upcoming discussion."
              : <><span className="font-medium text-slate-800">{alternativeTopic.name}</span> — Rate your experience with this material.</>
            }
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Rating Form */}
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
            <h2 className="text-lg font-medium text-slate-800 mb-4">
              Assess: {alternativeTopic.name}
            </h2>
            {!submitted && alternativeObjectives.length > 0 && (
              <div className="mb-5 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                <p className="text-xs font-semibold text-indigo-700 mb-1">
                  Rate this material in relation to the key points it was meant to cover:
                </p>
                <ul className="list-disc pl-5 space-y-0.5">
                  {alternativeObjectives.map((o, i) => (
                    <li key={i} className="text-xs text-indigo-900">{o.title}</li>
                  ))}
                </ul>
              </div>
            )}
            {submitted ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-slate-600 text-sm">Assessment submitted!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {questions.map((q) => (
                  <div key={q.key} className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">
                      {q.label}
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-24 text-right">
                        {q.lowLabel}
                      </span>
                      <div className="flex gap-1 flex-1 justify-center">
                        {[0, 1, 2, 3, 4, 5, 6].map((value) => (
                          <button
                            key={value}
                            onClick={() => handleRatingChange(q.key, value)}
                            className={`w-10 h-10 rounded-lg font-medium transition-all ${
                              ratings[q.key] === value
                                ? "bg-blue-600 text-white shadow-md scale-110"
                                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            }`}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                      <span className="text-xs text-slate-500 w-24">
                        {q.highLabel}
                      </span>
                    </div>
                  </div>
                ))}

                <div className="pt-4 border-t border-slate-200">
                  <button
                    onClick={handleSubmit}
                    disabled={!allAnswered}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                      allAnswered
                        ? "bg-blue-600 text-white hover:bg-blue-700 shadow-md"
                        : "bg-slate-200 text-slate-400 cursor-not-allowed"
                    }`}
                  >
                    {allAnswered ? "Submit Assessment" : "Please answer all questions"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Role Instructions (shown when showInstructions=true) */}
          {showInstructions && (
            <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
              <h2 className="text-lg font-medium text-slate-800 mb-4">
                Your Role: Student
              </h2>
              <div className="space-y-4 text-slate-700">
                <p>
                  In the next phase, you will learn about <span className="font-semibold text-blue-700">{topicName}</span> from your partner (the Teacher). Your partner has studied this topic, but you have not. You will both be quizzed on it, so use the discussion to learn what you need.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="font-medium text-blue-800 mb-2">Your task:</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-700 text-sm">
                    <li>Ask questions to understand the material</li>
                    <li>Request clarification when needed</li>
                    <li>Engage actively in the discussion</li>
                  </ul>
                </div>
                <p className="text-sm text-slate-500">
                  After the discussion, you will complete a quiz on what you learned.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer status */}
      <div className="bg-white border-t border-slate-200 px-6 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-center gap-2 text-sm text-slate-500">
          <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
          <span>The discussion will begin automatically when both participants are ready</span>
        </div>
      </div>
    </div>
  );
}
